import { ReactNode, useEffect, useRef } from 'react';
import { ErrorBoundaryInner } from '@/components/ErrorBoundaryInner';
import { useDbFunctions, useEvents } from '@/talons';
import { getOnStorage, setOnStorage } from '@/shared';

interface Props {
  children: ReactNode;
}

function ErrorBoundary({ children }: Props) {
  const db = useDbFunctions();
  const { registerEvent } = useEvents(db);
  const listenersRegisteredRef = useRef(false);
  const sessionStartedRef = useRef(false);

  const limitText = (value: unknown, maxLen: number) => {
    const str = String(value ?? '');
    if (str.length <= maxLen) return str;
    return `${str.slice(0, maxLen)}...[truncated:${str.length}]`;
  };

  const handleError = (error: Error, extra?: string) => {
    registerEvent({
      tipoEvento: 'BOUNDARY',
      obsEvento: `Erro capturado pelo ErrorBoundary: ${limitText(error.message, 500)} ${limitText(error.stack || '', 2000)}${extra ? ` ${limitText(extra, 800)}` : ''}`,
      stEvento: 0,
    });
  };

  useEffect(() => {
    if (!sessionStartedRef.current) {
      sessionStartedRef.current = true;
      const now = Date.now();
      const previous = getOnStorage('session_watchdog_v1');

      const navEntry = performance.getEntriesByType?.('navigation')?.[0] as PerformanceNavigationTiming | undefined;
      const navType = navEntry?.type || undefined;

      const previousLooksUnclean = !!previous?.sessionId && previous.running === true;

      if (previousLooksUnclean) {
        registerEvent({
          tipoEvento: 'BOUNDARY',
          obsEvento: `Sessão anterior terminou de forma inesperada (possível crash/OOM/reload). lastSeenAt=${new Date(previous.lastSeenAt).toISOString()} startedAt=${new Date(previous.startedAt).toISOString()} lastNavType=${previous.lastNavType || 'unknown'} currentNavType=${navType || 'unknown'}`,
          stEvento: 0,
        });
      }

      const sessionId = `${now}-${Math.random().toString(16).slice(2)}`;
      setOnStorage('session_watchdog_v1', {
        sessionId,
        startedAt: now,
        lastSeenAt: now,
        running: true,
        cleanExitAt: undefined,
        lastNavType: navType,
      });

      const markCleanExit = () => {
        const current = getOnStorage('session_watchdog_v1');
        if (!current?.sessionId || current.sessionId !== sessionId) return;
        setOnStorage('session_watchdog_v1', { ...current, lastSeenAt: Date.now(), running: false, cleanExitAt: Date.now() });
      };

      window.addEventListener('pagehide', markCleanExit);
      window.addEventListener('beforeunload', markCleanExit);

      return () => {
        window.removeEventListener('pagehide', markCleanExit);
        window.removeEventListener('beforeunload', markCleanExit);
      };
    }

    if (listenersRegisteredRef.current) return;
    listenersRegisteredRef.current = true;

    const onWindowError = (event: Event) => {
      if (event instanceof ErrorEvent) {
        const err = event.error instanceof Error ? event.error : new Error(String(event.message || 'window.onerror'));
        const meta = [
          event.filename ? `file=${event.filename}` : null,
          Number.isFinite(event.lineno) ? `line=${event.lineno}` : null,
          Number.isFinite(event.colno) ? `col=${event.colno}` : null,
        ]
          .filter(Boolean)
          .join(' ');
        handleError(err, meta ? `[${meta}]` : undefined);
        return;
      }

      const anyEvent = event as any;
      const target = anyEvent?.target as HTMLElement | undefined;
      const tag = target?.tagName ? String(target.tagName).toLowerCase() : '';
      const src = (target as any)?.src || (target as any)?.href;
      const details = [tag ? `tag=${tag}` : null, src ? `src=${String(src)}` : null].filter(Boolean).join(' ');
      handleError(new Error('Erro de recurso capturado em window.error'), details ? `[${details}]` : undefined);
    };

    const onUnhandledRejection = (event: PromiseRejectionEvent) => {
      const reason = (event as any)?.reason;
      let reasonText = '';
      if (typeof reason === 'string') reasonText = reason;
      else {
        try {
          reasonText = JSON.stringify(reason);
        } catch {
          reasonText = String(reason);
        }
      }
      const err =
        reason instanceof Error ? reason : new Error(limitText(reasonText || 'unhandledrejection', 1200));
      handleError(err, '[unhandledrejection]');
    };

    window.addEventListener('error', onWindowError);
    window.addEventListener('unhandledrejection', onUnhandledRejection);

    return () => {
      window.removeEventListener('error', onWindowError);
      window.removeEventListener('unhandledrejection', onUnhandledRejection);
    };
  }, []);

  return (
    <ErrorBoundaryInner
      fallback={
        <div
          style={{
            minHeight: '100vh',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '24px',
            backgroundColor: '#f8fafc',
          }}
        >
          <div
            style={{
              width: '100%',
              maxWidth: '520px',
              backgroundColor: '#ffffff',
              border: '1px solid #e2e8f0',
              borderRadius: '8px',
              padding: '24px',
              textAlign: 'center',
            }}
          >
            <h1 style={{ margin: '0 0 12px', fontSize: '1.5rem', color: '#0f172a' }}>
              Ocorreu um erro inesperado
            </h1>
            <p style={{ margin: '0 0 20px', color: '#334155' }}>
              Tivemos um problema ao carregar esta tela. Tente recarregar a página.
            </p>
            <div style={{ display: 'flex', gap: '12px', justifyContent: 'center', flexWrap: 'wrap' }}>
              <button
                type="button"
                onClick={() => window.location.reload()}
                style={{
                  border: 'none',
                  borderRadius: '6px',
                  backgroundColor: '#0f172a',
                  color: '#ffffff',
                  padding: '10px 16px',
                  cursor: 'pointer',
                }}
              >
                Recarregar página
              </button>
              <button
                type="button"
                onClick={() => {
                  window.location.href = '/login';
                }}
                style={{
                  border: '1px solid #cbd5e1',
                  borderRadius: '6px',
                  backgroundColor: '#ffffff',
                  color: '#0f172a',
                  padding: '10px 16px',
                  cursor: 'pointer',
                }}
              >
                Ir para login
              </button>
            </div>
          </div>
        </div>
      }
      onError={(error, errorInfo) => handleError(error, errorInfo?.componentStack ? `[componentStack] ${errorInfo.componentStack}` : undefined)}
    >
      {children}
    </ErrorBoundaryInner>
  );
}

export default ErrorBoundary;
